import { io, Socket } from 'socket.io-client';

import Logger from '@/core/utils/logging';
import {
    JoinRoomPayload,
    ROOM_EVENT_NAMES,
    SocketPayload,
    SocketRoomType,
} from '@/core/types/Socket';

import BaseService from './BaseService';

// 3 minutes — the established contract for SCA/move-in socket waits.
const DEFAULT_TIMEOUT_MS = 180_000;

export interface ListenOptions<T extends SocketRoomType> {
    room: string;
    roomType: T;
    onEvent: (payload: SocketPayload<T>) => void;
    onTimeout?: () => void;
    timeoutMs?: number;
}

/**
 * Generic Socket.IO client for RapidStor rooms. Connects to the API base URL, joins a
 * room by `roomType`, and invokes `onEvent` once with the room's completion payload (the
 * server replays the latest stored result on join, so a late join still resolves). A
 * `settled` guard ensures `onEvent`/`onTimeout` fire at most once. One live socket per
 * instance — call `disconnect()` (or let the owning hook clean up) when done.
 */
export default class SocketService extends BaseService {
    private socket: Socket | null = null;

    private settled = false;

    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    listen<T extends SocketRoomType>({
        room,
        roomType,
        onEvent,
        onTimeout,
        timeoutMs = DEFAULT_TIMEOUT_MS,
    }: ListenOptions<T>): void {
        // Only ever hold one live socket per instance.
        this.disconnect();
        this.settled = false;

        const baseUrl = window.rapidstor_settings?.rapidstor_options?.baseURL;
        if (!baseUrl) {
            Logger.warn('[SocketService] baseURL not initialised; cannot open socket');
            return;
        }

        const socket = io(baseUrl, { transports: ['websocket', 'polling'] });
        this.socket = socket;

        const eventName = ROOM_EVENT_NAMES[roomType];
        const joinPayload: JoinRoomPayload = { room, roomType };

        socket.on('connect', () => {
            Logger.info(`[SocketService] Socket connected, joining room ${room} (type ${roomType})`);
            socket.emit('joinRoom', joinPayload);
        });

        // `eventName` is a computed union; cast to a plain string so it resolves to the
        // generic listener overload rather than the reserved-event one.
        socket.on(eventName as string, (payload: unknown) => {
            if (this.settled) {
                return;
            }
            this.settled = true;
            this.clearTimer();
            onEvent(payload as SocketPayload<T>);
        });

        socket.on('connect_error', (error: Error) => {
            Logger.error(`[SocketService] connect_error for room ${room}: ${error.message}`);
        });

        socket.on('roomJoinError', () => {
            Logger.error(`[SocketService] roomJoinError for room ${room} (roomType ${roomType})`);
        });

        this.timeoutId = setTimeout(() => {
            if (this.settled) {
                return;
            }
            this.settled = true;
            onTimeout?.();
            this.disconnect();
        }, timeoutMs);
    }

    disconnect(): void {
        this.clearTimer();
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }
    }

    private clearTimer(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
}
